import { useRoomContext } from '@livekit/components-react'
import {
  type LocalAudioTrack,
  type LocalTrackPublication,
  type LocalVideoTrack,
  RoomEvent,
  Track,
} from 'livekit-client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { audioVerdict } from './audioCheck'
import { classifyDeviceError, type DeviceAccess } from './deviceProbe'
import { isDeviceSwitchInFlight } from './devices'
import { report } from './diagnostics'
import { readLinkHealth } from './MediaReporter'
import { micWalkthroughFault } from './micFault'
import { shouldClearMicSilent } from './micSilence'
import {
  cameraLooksFrozen,
  freezeWatchPaused,
  isVerifiedCameraRecovery,
  JOIN_GRACE_MS,
  joinPublishMissing,
  nextCameraRecovery,
  type RecoverLabel,
  recoverLabel,
  recoverProof,
  senderFramesAdvanced,
  senderFramesLookLive,
  sendLooksStalled,
  shouldAcquireAfterPermissionGrant,
  shouldActOnStaleStats,
} from './publishHealth'
import { shouldCountManualAttempt } from './recoveryGate'

/**
 * Keeping a lesson alive when a device disappears underneath it.
 *
 * WHY. On 2026-08-02 the product could DETECT device faults but could not RECOVER
 * from them, and detection alone loses the minute anyway: a teacher who knows the
 * microphone is broken and can do nothing about it still ends up on another calling app. There was
 * no `devicechange` handling anywhere in the app, so a child who unplugged a USB
 * headset mid-lesson stayed "in the room" publishing a dead track while the teacher
 * heard silence.
 *
 * Measured platform reality drives the design: every student datapoint we have is
 * desktop Chromium — Chrome and Edge, mostly Windows. On a desktop, headsets get plugged and
 * unplugged constantly, laptops sleep, and permissions get changed in another tab
 * — so device churn mid-lesson is the norm rather than an edge case.
 *
 * THE PRIMITIVE IS `LocalTrack.restartTrack()`. It re-acquires the underlying
 * MediaStreamTrack on the EXISTING publication: no renegotiation, no republish,
 * and — the part that matters most — no new join identity. Every recovery path
 * here stays inside the session the student already has. Nothing in this module
 * mints a join code, touches credentials, or re-enters the room; that is
 * deliberate, because the credential path is where a bug costs a whole lesson
 * rather than a few seconds of audio.
 *
 * It also means we do not call getUserMedia ourselves to diagnose: restartTrack
 * does the acquire, and if it throws we classify ITS error with the same taxonomy
 * the prejoin uses. One acquire, one permission surface, one source of truth.
 */

/** Why a recovery ran — kept coarse, and reported for every attempt. */
export type RecoveryReason =
  | 'devicechange'
  | 'track_ended'
  | 'visibility'
  | 'permission_granted'
  | 'manual'
  | 'join'
  | 'frozen'
  | 'send_stalled'

export interface RecoveryState {
  /** A recovery is in flight; the UI says "reconnecting", never "broken". */
  running: boolean
  /** Outcome of the last attempt, or null before the first one. */
  lastAccess: DeviceAccess | null
  /** Honest transport/device verdict for the last attempt. */
  lastLabel: RecoverLabel | null
  /** Per-device evidence for recovery feedback. */
  lastMicAccess: DeviceAccess | null
  lastCamAccess: DeviceAccess | null
  /** The existing camera watchdog exhausted recovery without fresh frames. */
  cameraFrozen: boolean
  /** Bumped on every completed attempt so a toast can re-show for a NEW event. */
  attempts: number
}

/**
 * Device events arrive in bursts — Windows emits several `devicechange` events for
 * one physical unplug — so the trigger is debounced and every path funnels through
 * a single-flight guard. Without both, one headset swap becomes four concurrent
 * getUserMedia calls and a permission-prompt storm.
 */
const DEBOUNCE_MS = 400
/** A track can go briefly `muted` on a device glitch; only a sustained mute is real. */
const MUTE_DWELL_MS = 2000

/** Narrowing helper — only audio tracks can be asked about silence. */
const isAudio = (track: LocalAudioTrack | LocalVideoTrack): track is LocalAudioTrack =>
  track.kind === Track.Kind.Audio

interface Options {
  credential: { code?: string | null; reconnectToken?: string | null }
  /** Publishes fresh mic/cam taxonomy so the teacher's roster stops showing stale truth. */
  onAccess: (mic: DeviceAccess | null, cam: DeviceAccess | null) => void
  /** False for a Ghost observer, which publishes nothing and has nothing to recover. */
  enabled: boolean
  /**
   * A device fault the user could plausibly fix themselves, raised so the caller
   * can offer the walkthrough mid-lesson — and `null` the moment a recovery
   * clears it, so the walkthrough closes itself instead of making someone who
   * has already fixed their headset dismiss a stale warning.
   */
  onFault?: (access: DeviceAccess | null) => void
  /** What they asked for at join — not `isMicrophoneEnabled` (false when unpublished). */
  intended: { mic: boolean; cam: boolean }
}

export function useMediaRecovery({
  credential,
  onAccess,
  enabled,
  onFault,
  intended,
}: Options): RecoveryState & {
  recover: (reason: RecoveryReason) => void
} {
  const room = useRoomContext()
  const [state, setState] = useState<RecoveryState>({
    running: false,
    lastAccess: null,
    lastLabel: null,
    lastMicAccess: null,
    lastCamAccess: null,
    cameraFrozen: false,
    attempts: 0,
  })
  const inFlight = useRef(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const muteTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const silenceWatch = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  // Held in refs so `recover` — and therefore `schedule` and every listener that
  // closes over it — keeps a stable identity. A prop identity change must not
  // re-arm the debounce or drop a recovery that is already pending.
  const onAccessRef = useRef(onAccess)
  onAccessRef.current = onAccess
  const onFaultRef = useRef(onFault)
  onFaultRef.current = onFault
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const intendedRef = useRef(intended)
  intendedRef.current = intended
  const frozenStreak = useRef(0)
  const cameraFaulted = useRef(false)
  const audio60Reported = useRef(false)

  const recover = useCallback(
    (reason: RecoveryReason) => {
      if (!enabled || !room) return
      // Single-flight. A burst of devicechange events, a track ending, and the tab
      // regaining focus can all fire within the same second for one unplug.
      if (inFlight.current) return
      // switchActiveDevice stops the old capture before publishing the new one.
      // Its transient ended/devicechange signals are not faults; racing a restart
      // here would acquire the same headset twice. A deliberate Retry still wins.
      if (isDeviceSwitchInFlight() && reason !== 'manual') return

      const local = room.localParticipant
      const audio = local.getTrackPublication(Track.Source.Microphone)?.track
      const video = local.getTrackPublication(Track.Source.Camera)?.track as
        | LocalVideoTrack
        | undefined
      // Nothing published means nothing to restart — a student who joined muted has
      // no dead track to repair, and forcing a capture here would be an unasked-for
      // permission prompt mid-lesson. But a manual "Fix now" must still COUNT even
      // with nothing to repair: manual recovery feedback counts `attempts`, so a session
      // that lost every track would otherwise lose feedback for the manual attempt. Count the
      // press (no getUserMedia, no publish); automatic triggers stay silent no-ops.
      if (!audio && !video && reason !== 'join') {
        if (
          shouldCountManualAttempt({ reason, hasAudio: Boolean(audio), hasVideo: Boolean(video) })
        ) {
          setState((s) => ({ ...s, lastAccess: null, attempts: s.attempts + 1 }))
        }
        return
      }

      // Nothing is wrong — so do not announce a repair, or measure one.
      //
      // `visibilitychange` fires on every alt-tab, and a teacher switching between
      // the class and their teaching material fires it constantly. Production on
      // 2026-08-03 showed 52 recoveries in 40 minutes across four 1:1 classes,
      // every one of them a no-op that still: wrote TWO telemetry rows
      // (recover=start then recover=success with mic=n/a,cam=n/a), and flipped
      // `running` true, which flashes the "reconnecting your microphone…" toast —
      // a reassuring message about a problem nobody had, announced politely to
      // screen readers each time.
      //
      // The cost was real in both directions: those 102 rows were 58% of all
      // `permissions` telemetry for the window, burying the handful of genuine
      // device verdicts we collect it for.
      //
      // `manual` always proceeds: a person pressing Retry is owed an actual
      // attempt, even when the tracks look fine to us (see `forced` below).
      const needsAttention = [audio, video].some((track) => {
        if (!track) return false
        const raw = track.mediaStreamTrack
        return !raw || raw.readyState !== 'live' || Boolean(raw.muted)
      })
      const want = intendedRef.current
      const publishGap = {
        micIntended: want.mic,
        camIntended: want.cam,
        hasMicPub: Boolean(audio),
        hasCamPub: Boolean(video),
      }
      const joinGap = reason === 'join' && joinPublishMissing(publishGap)
      const grantGap = shouldAcquireAfterPermissionGrant({ reason, ...publishGap })
      const cameraRecovery = isVerifiedCameraRecovery(reason)
      if (
        cameraRecovery &&
        (!video || video.isMuted || !intendedRef.current.cam || cameraFaulted.current)
      )
        return
      if (!needsAttention && !joinGap && !grantGap && reason !== 'manual' && !cameraRecovery) return

      inFlight.current = true
      setState((s) => ({ ...s, running: true }))
      report(credentialRef.current, {
        stage: 'permissions',
        permission: `recover=start,reason=${reason}`,
      })

      const senderFrameCount = async (track: LocalVideoTrack) =>
        (await track.getSenderStats()).reduce(
          (total, layer) => total + (layer.framesSent ?? Number.NaN),
          0,
        )

      let proofRan = false
      let sendLooksLive: boolean | null = null
      const senderResumed = async (track: LocalVideoTrack) => {
        proofRan = true
        const before = await senderFrameCount(track)
        await new Promise<void>((resolve) => setTimeout(resolve, 2000))
        return (sendLooksLive = senderFramesLookLive(before, await senderFrameCount(track)))
      }

      const republishCamera = async (track: LocalVideoTrack) => {
        if (!intendedRef.current.cam || track.isMuted) return false
        await local.setCameraEnabled(false)
        const publication = await local.setCameraEnabled(true)
        const republished = (publication?.track ??
          local.getTrackPublication(Track.Source.Camera)?.track) as LocalVideoTrack | undefined
        if (!republished) return false
        if (!intendedRef.current.cam || republished.isMuted) {
          await republished.mute()
          return false
        }
        return senderResumed(republished)
      }

      const recoverCamera = async (track: LocalVideoTrack): Promise<DeviceAccess> => {
        if (!isVerifiedCameraRecovery(reason)) return 'unknown'
        const step = nextCameraRecovery({
          reason,
          verifiedRestartFailed: false,
          consecutive: frozenStreak.current,
        })
        if (step === 'fault') return 'unknown'
        if (step === 'restart') {
          try {
            await track.restartTrack()
            if (track.isMuted) await track.mute()
            if (await senderResumed(track)) return 'ok'
          } catch {
            // One full republish is the last in-call recovery before surfacing a fault.
          }
          if (
            nextCameraRecovery({
              reason,
              verifiedRestartFailed: true,
              consecutive: frozenStreak.current,
            }) === 'fault'
          ) {
            return 'unknown'
          }
        }
        try {
          return (await republishCamera(track)) ? 'ok' : 'unknown'
        } catch {
          return 'unknown'
        }
      }

      const restart = async (track: LocalAudioTrack | LocalVideoTrack | undefined) => {
        if (!track) return null
        const raw = track.mediaStreamTrack

        // TWO DIFFERENT MUTES, and I had them backwards.
        //
        //   track.isMuted   LiveKit APPLICATION mute — the child chose to be quiet.
        //                   Never a reason to re-acquire. Restarting here would yank
        //                   the device out from under a child who deliberately muted
        //                   themselves, on something as ordinary as alt-tabbing back.
        //   raw.muted       The DEVICE stopped delivering data: hardware mute switch,
        //                   unplugged input, OS took it away. readyState usually
        //                   stays 'live' through this, which is exactly why
        //                   readyState alone cannot detect it — and why the
        //                   mute-dwell trigger used to schedule a recovery that
        //                   no-opped and then reported a false success.
        //
        // Repair when the capture is gone or silent at the DEVICE level; leave
        // application mute strictly alone.
        //
        // `manual` is the exception, and it has to be: a live, unmuted track that
        // produces pure silence passes both tests below, so a user who had just
        // un-muted their headset and pressed Retry got an instant "success" over
        // the same dead device — the button reported a repair it had not even
        // attempted. When a person explicitly asks, always re-open the device.
        //
        // But NOT while they are application-muted. `restartTrack` republishes
        // live and the re-mute below is a separate await, so every forced restart
        // opens a brief window where audio really does leave the device — which
        // is a fine trade for repairing a broken capture and an unacceptable one
        // for a child who deliberately muted themselves. There is also nothing to
        // learn: a muted track is silent by design, so the check would be
        // measuring our own mute.
        //
        // AUDIO ONLY. `recover` walks the mic and the camera with the same
        // reason, so forcing on `manual` alone bounced a perfectly good camera on
        // every Retry — a black tile for every student in the class to repair a
        // microphone. A broken camera is still repaired by the two tests below.
        const dead = !raw || raw.readyState !== 'live'
        const deviceSilent = Boolean(raw?.muted)
        const forced = reason === 'manual' && !track.isMuted && isAudio(track)

        // `null`, NOT 'ok' — and the distinction is load-bearing.
        //
        // Reaching here means the capture looked untouched, so we did not
        // re-acquire and therefore learned NOTHING. Returning 'ok' made that
        // silence into a positive claim, and it clobbered a real one: a digitally
        // silent microphone is `live` and not `raw.muted`, so it lands in exactly
        // this branch. The first alt-tab (visibility), unrelated unplug
        // (devicechange) or permission change after a silence fault therefore
        // published mic=ok over the fault and closed the walkthrough, while the
        // child was still inaudible. `null` means "no new evidence": callers leave
        // the previous verdict standing.
        if (!dead && !deviceSilent && !forced) return null

        await track.restartTrack()
        // A restart republishes live, which would silently un-mute a child who had
        // chosen to be quiet. Re-assert their choice.
        if (track.isMuted) await track.mute()

        // Did the re-acquire actually fix the silence? `restartTrack` re-runs
        // livekit's own silence check but only the PUBLISH path emits the room
        // event, so a manual retry would otherwise never learn its own outcome
        // and the walkthrough would sit there claiming a fault we had just
        // cleared (or, worse, declare success over one we had not). Ask directly.
        if (forced) {
          // Fails CLOSED. A probe that throws tells us nothing, and the person
          // pressing Retry is already living with a fault — clearing it on "we
          // could not check" would announce a repair we never observed.
          const stillSilent = await track.checkForSilence().catch(() => true)
          if (stillSilent) return 'silent' as DeviceAccess
        }
        return 'ok' as DeviceAccess
      }

      void (async () => {
        let micAccess: DeviceAccess | null = null
        let camAccess: DeviceAccess | null = null
        let worst: DeviceAccess = 'ok'
        let label: RecoverLabel = 'degraded'
        // The ENTIRE body is wrapped: anything that escapes here — a restart, a
        // classify, the reporting — would otherwise leave inFlight true forever,
        // silently disabling every future recovery for the rest of the lesson and
        // stranding the "reconnecting" toast on a child's screen.
        try {
          if (reason === 'join' || reason === 'permission_granted') {
            try {
              if (intendedRef.current.mic && !local.getTrackPublication(Track.Source.Microphone)) {
                await local.setMicrophoneEnabled(true)
              }
            } catch (error) {
              micAccess = classifyDeviceError(error).access
            }
            try {
              if (intendedRef.current.cam && !local.getTrackPublication(Track.Source.Camera)) {
                await local.setCameraEnabled(true)
              }
            } catch (error) {
              camAccess = classifyDeviceError(error).access
            }
          }
          const audioNow = local.getTrackPublication(Track.Source.Microphone)?.track as
            | LocalAudioTrack
            | undefined
          const videoNow = local.getTrackPublication(Track.Source.Camera)?.track as
            | LocalVideoTrack
            | undefined
          if (reason !== 'send_stalled') {
            try {
              micAccess = micAccess ?? (await restart(audioNow))
            } catch (error) {
              micAccess = classifyDeviceError(error).access
            }
          }
          try {
            camAccess =
              camAccess ??
              (cameraRecovery ? await recoverCamera(videoNow!) : await restart(videoNow))
          } catch (error) {
            camAccess = cameraRecovery ? 'unknown' : classifyDeviceError(error).access
          }
          const proofTrack = (videoNow ?? local.getTrackPublication(Track.Source.Camera)?.track) as
            | LocalVideoTrack
            | undefined
          if (
            !proofRan &&
            intendedRef.current.cam &&
            (camAccess === 'ok' || camAccess === null) &&
            proofTrack?.mediaStreamTrack?.readyState === 'live' &&
            !proofTrack.isMuted
          ) {
            await senderResumed(proofTrack)
          }
          worst =
            micAccess && micAccess !== 'ok'
              ? micAccess
              : camAccess && camAccess !== 'ok'
                ? camAccess
                : 'ok'
          label = recoverLabel({
            hasPeer: room.remoteParticipants.size > 0,
            sendLooksLive,
            deviceFail: worst !== 'ok',
          })
          // Tell the room even when recovery FAILED: a teacher seeing "browser is
          // blocking their microphone" can coach, while one seeing a stale "ok"
          // reaches for another calling app.
          onAccessRef.current(micAccess, camAccess)
          const proof = recoverProof(proofRan)
          report(credentialRef.current, {
            stage: 'permissions',
            outcome:
              label === 'success' || label === 'local_only' || label === 'unverified'
                ? 'ok'
                : 'error',
            permission: `recover=${label},reason=${reason},mic=${micAccess ?? 'n/a'},cam=${camAccess ?? 'n/a'}${proof ? `,proof=${proof}` : ''}`,
          })
          // Until now a failed recovery ended at the teacher's roster: the student
          // saw the reassuring "reconnecting" toast disappear and then nothing,
          // with a dead microphone and no way to find out why. Hand the verdict
          // up so the walkthrough that already existed for the prejoin can be
          // offered mid-lesson — and withdrawn again on success. Silence when
          // there was no audio track at all: nothing was tested, so nothing is
          // known, and overwriting a real fault with `null` would hide it.
          if (micAccess) onFaultRef.current?.(micWalkthroughFault(micAccess))
        } finally {
          inFlight.current = false
          if (cameraRecovery) {
            if (camAccess === 'ok') {
              frozenStreak.current = 0
              cameraFaulted.current = false
            } else {
              frozenStreak.current += 1
              cameraFaulted.current = true
            }
          }
          setState((s) => ({
            running: false,
            lastAccess: worst,
            lastLabel: label,
            lastMicAccess: micAccess,
            lastCamAccess: camAccess,
            cameraFrozen: cameraFaulted.current,
            attempts: s.attempts + 1,
          }))
        }
      })()
    },
    [enabled, room],
  )

  const schedule = useCallback(
    (reason: RecoveryReason) => {
      if (debounce.current) clearTimeout(debounce.current)
      debounce.current = setTimeout(() => recover(reason), DEBOUNCE_MS)
    },
    [recover],
  )

  // A device appearing or disappearing. On Windows one physical unplug emits
  // several of these, which is exactly what the debounce is for.
  useEffect(() => {
    if (!enabled) return
    const media = navigator.mediaDevices
    if (!media?.addEventListener) return
    const handler = () => schedule('devicechange')
    media.addEventListener('devicechange', handler)
    return () => media.removeEventListener('devicechange', handler)
  }, [enabled, schedule])

  /**
   * Bumped whenever the set of local publications changes, so the binding effect
   * below re-runs against tracks that did not exist when it first ran.
   *
   * LiveKit publishes the initial microphone and camera on SignalConnected,
   * which is not ordered against this component mounting — so the common case,
   * an ordinary join, is exactly the one where the first pass can find nothing
   * to bind to.
   */
  const [publishTick, setPublishTick] = useState(0)
  useEffect(() => {
    if (!enabled || !room) return
    const bump = () => setPublishTick((tick) => tick + 1)
    room.on(RoomEvent.LocalTrackPublished, bump)
    room.on(RoomEvent.LocalTrackUnpublished, bump)
    return () => {
      room.off(RoomEvent.LocalTrackPublished, bump)
      room.off(RoomEvent.LocalTrackUnpublished, bump)
    }
  }, [enabled, room])

  useEffect(() => {
    if (!enabled || !room || audio60Reported.current) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false
    const arm = () => {
      if (audio60Reported.current || timer || room.remoteParticipants.size === 0) return
      void readLinkHealth(room).then((before) => {
        if (cancelled || audio60Reported.current) return
        timer = setTimeout(() => {
          void (async () => {
            const local = room.localParticipant.getTrackPublication(Track.Source.Microphone)
            const localTrack = local?.track as LocalAudioTrack | undefined
            const micSilent = Boolean(
              localTrack &&
                !localTrack.isMuted &&
                (await localTrack.checkForSilence().catch(() => false)),
            )
            const remoteMics = [...room.remoteParticipants.values()].map((participant) =>
              participant.getTrackPublication(Track.Source.Microphone),
            )
            const after = await readLinkHealth(room)
            const verdict = audioVerdict({
              intendedMic: intendedRef.current.mic,
              micPublished: Boolean(localTrack),
              micMuted: Boolean(localTrack?.isMuted),
              micSilent,
              playbackBlocked: !room.canPlaybackAudio,
              remoteMicPublished: remoteMics.some(Boolean),
              remoteMicMuted:
                remoteMics.length > 0 && remoteMics.every((publication) => publication?.isMuted),
              inboundAudioBytesDelta:
                before && after ? after.inboundAudioBytes - before.inboundAudioBytes : undefined,
              inboundAudioEnergyDelta:
                before && after ? after.inboundAudioEnergy - before.inboundAudioEnergy : undefined,
            })
            audio60Reported.current = true
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: verdict.fault ? 'error' : 'ok',
              permission: `audio60=in:${verdict.in},out:${verdict.out}`,
            })
            if (verdict.out === 'silent') {
              onAccessRef.current('silent', null)
              onFaultRef.current?.('silent')
            }
          })()
        }, 60_000)
      })
    }
    arm()
    room.on(RoomEvent.ParticipantConnected, arm)
    return () => {
      cancelled = true
      room.off(RoomEvent.ParticipantConnected, arm)
      if (timer) clearTimeout(timer)
    }
  }, [enabled, room])

  // The authoritative signal that a capture is gone: the spec models both device
  // ejection and permission revocation as track-ending events, so this fires in
  // cases `devicechange` does not.
  useEffect(() => {
    if (!enabled || !room) return
    const tracks = () =>
      [Track.Source.Microphone, Track.Source.Camera]
        .map((source) => room.localParticipant.getTrackPublication(source)?.track)
        .filter((track): track is LocalAudioTrack | LocalVideoTrack => Boolean(track))

    const cleanups: Array<() => void> = []
    for (const track of tracks()) {
      const raw = track.mediaStreamTrack
      if (!raw) continue
      const onEnded = () => schedule('track_ended')
      // A brief mute is normal device noise; only a sustained one is a fault worth
      // a re-acquire, so it has to dwell before we act.
      const onMute = () => {
        const timer = setTimeout(() => schedule('track_ended'), MUTE_DWELL_MS)
        muteTimers.current.set(raw.id, timer)
      }
      const onUnmute = () => {
        const timer = muteTimers.current.get(raw.id)
        if (timer) clearTimeout(timer)
        muteTimers.current.delete(raw.id)
      }
      raw.addEventListener('ended', onEnded)
      raw.addEventListener('mute', onMute)
      raw.addEventListener('unmute', onUnmute)
      cleanups.push(() => {
        raw.removeEventListener('ended', onEnded)
        raw.removeEventListener('mute', onMute)
        raw.removeEventListener('unmute', onUnmute)
      })
    }
    return () => {
      cleanups.forEach((fn) => fn())
      muteTimers.current.forEach((timer) => clearTimeout(timer))
      muteTimers.current.clear()
    }
    // attempts is in the deps so listeners re-bind to the NEW MediaStreamTrack after
    // a successful restart — otherwise recovery works exactly once.
    //
    // publishTick is in the deps for a blunter reason: on an ordinary join this
    // effect can run BEFORE the local tracks are published. `getTrackPublication`
    // then returns nothing, the loop binds nothing, and — with no other signal to
    // re-run — this entire module stays inert for the whole lesson. The recovery
    // that exists precisely for a headset unplugged mid-class would simply never
    // have been armed, and nothing would have said so.
  }, [enabled, room, schedule, state.attempts, publishTick])

  // A LIVE microphone that carries nothing but silence.
  //
  // livekit-client already looks for this — it runs `checkForSilence()` on every
  // published audio track and emits `LocalAudioSilenceDetected` — and the app
  // simply never listened, so the single most Windows-shaped audio fault we have
  // was being detected by the SDK and thrown away. Nothing else in this module
  // can catch it: the track is `live`, it is not `muted`, no getUserMedia error
  // was raised, and every existing trigger here is a form of "the capture went
  // away". This is the opposite — the capture is fine and empty.
  //
  // Deliberately NOT wired to `schedule`: `restartTrack` re-acquires the same
  // silent endpoint, so it would cost a real audio interruption to change
  // nothing. A person has to flip the switch, so the only useful response is to
  // say so — loudly, to the student, and in the taxonomy the teacher's roster
  // already reads.
  useEffect(() => {
    if (!enabled || !room) return
    const onSilence = (pub: LocalTrackPublication) => {
      // THE MICROPHONE ONLY. livekit runs its silence probe on every local audio
      // track it publishes, and since shares carry sound there is now a second
      // one: a teacher who starts sharing a tab BEFORE pressing play — which is the
      // normal order — publishes a momentarily silent ScreenShareAudio track. The
      // 200 ms probe finds silence, and without this line we would tell that teacher
      // their microphone is broken and flag them as faulty in every roster in the
      // room, mid-lesson, because they did nothing wrong.
      //
      // The general rule this is an instance of: `Track.Source.Microphone` is the
      // only signal for microphone health. Nothing about share audio says anything
      // about whether a person can be heard.
      if (pub.source !== Track.Source.Microphone) return
      // A muted track IS digital silence, and livekit does not check mute before
      // running its detector — so this fires for a deliberate mute too. The path
      // that matters is a reconnect: `republishAllTracks` re-publishes a muted
      // mic, `onLocalTrackPublished` measures it, finds silence, and we would
      // have opened "your microphone isn't picking up sound" on a child who muted
      // themselves on purpose. Nothing is wrong and there is nothing to fix.
      if (pub.track?.isMuted) return
      report(credentialRef.current, {
        stage: 'permissions',
        outcome: 'error',
        permission: 'mic=silent',
      })
      onAccessRef.current('silent', null)
      onFaultRef.current?.('silent')
      const track = pub.track as LocalAudioTrack | undefined
      if (!track) return
      if (silenceWatch.current) clearInterval(silenceWatch.current)
      silenceWatch.current = setInterval(() => {
        if (track.isMuted) return
        void track
          .checkForSilence()
          .then((stillSilent) => {
            if (!shouldClearMicSilent({ muted: track.isMuted, stillSilent })) return
            if (silenceWatch.current) clearInterval(silenceWatch.current)
            silenceWatch.current = undefined
            report(credentialRef.current, { stage: 'permissions', permission: 'mic=ok' })
            onAccessRef.current('ok', null)
            onFaultRef.current?.(null)
          })
          .catch(() => undefined)
      }, 3000)
    }
    room.on(RoomEvent.LocalAudioSilenceDetected, onSilence)
    return () => {
      room.off(RoomEvent.LocalAudioSilenceDetected, onSilence)
      if (silenceWatch.current) clearInterval(silenceWatch.current)
    }
  }, [enabled, room])

  // Coming back from a sleeping laptop or a backgrounded tab. Chromium throttles
  // timers in the background, so the honest moment to check health is the moment
  // the lesson is on screen again.
  //
  // This matters more on EDGE than on Chrome, and Edge is the Windows default:
  // Edge's "sleeping tabs" feature discards background tabs to save memory. It
  // normally spares a tab that is capturing audio or video, but a student who is
  // muted with their camera off does not obviously look like one — and a page
  // cannot opt out of sleeping tabs, so waking up correctly is the only defence
  // available to us.
  useEffect(() => {
    if (!enabled) return
    const handler = () => {
      if (document.visibilityState === 'visible') schedule('visibility')
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [enabled, schedule])

  // A parent fixing the permission in browser settings while the lesson runs. This
  // is the transition tonight's whole story turns on: without it the child must
  // reload, and reloading is what burns join codes and produces "this link is no
  // longer valid".
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const permissions = navigator.permissions
    if (!permissions?.query) return

    const detaches: Array<() => void> = []
    const watch = (name: PermissionName) => {
      void permissions
        .query({ name })
        .then((result) => {
          const onChange = () => {
            if (result.state === 'granted') schedule('permission_granted')
          }
          if (cancelled) return
          result.addEventListener('change', onChange)
          detaches.push(() => result.removeEventListener('change', onChange))
        })
        .catch(() => undefined)
    }
    watch('microphone' as PermissionName)
    watch('camera' as PermissionName)

    return () => {
      cancelled = true
      for (const off of detaches) off()
    }
  }, [enabled, schedule])

  // Join can succeed with audio/video intended ON and nothing published
  // (getUserMedia failed, student still let in). Wait for LiveKit's first
  // publish, then retry once.
  useEffect(() => {
    if (!enabled || !room) return
    const timer = setTimeout(() => recover('join'), JOIN_GRACE_MS)
    return () => clearTimeout(timer)
  }, [enabled, room, recover])

  // Camera track still "on" but pictures stopped (4125283: 75s hole in VP8).
  useEffect(() => {
    if (!enabled || !room) return
    const video = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track as
      | LocalVideoTrack
      | undefined
    if (!video || video.isMuted) return
    const el = video.attach() as HTMLVideoElement
    el.muted = true
    el.playsInline = true
    void el.play().catch(() => undefined)
    let watchingSince = Date.now()
    let lastFrameAt: number | null = null
    let lastDecoded = 0
    let lastSent = 0
    let lastSentAt: number | null = null
    let rvfc = 0
    let cancelled = false
    const canRafc = typeof el.requestVideoFrameCallback === 'function'
    const canQuality = typeof el.getVideoPlaybackQuality === 'function'
    const markFrame = () => {
      lastFrameAt = Date.now()
      frozenStreak.current = 0
    }
    const onFrame = () => {
      markFrame()
      if (canRafc) rvfc = el.requestVideoFrameCallback(onFrame)
    }
    if (canRafc) rvfc = el.requestVideoFrameCallback(onFrame)
    const tick = setInterval(() => {
      if (video.isMuted) return
      if (freezeWatchPaused(document.hidden)) {
        lastFrameAt = null
        lastSentAt = null
        watchingSince = Date.now()
        return
      }
      if (canQuality) {
        const n = el.getVideoPlaybackQuality().totalVideoFrames
        if (n > lastDecoded) {
          markFrame()
          lastDecoded = n
        }
      }
      void video
        .getSenderStats()
        .then((stats) => {
          if (!shouldActOnStaleStats(cancelled)) return
          const sent = stats.reduce((n, layer) => n + (layer.framesSent ?? Number.NaN), 0)
          const now = Date.now()
          if (senderFramesAdvanced(lastSent, sent)) {
            lastSent = sent
            lastSentAt = now
            frozenStreak.current = 0
            cameraFaulted.current = false
            setState((s) => (s.cameraFrozen ? { ...s, cameraFrozen: false } : s))
          }
          if (
            !video.isMuted &&
            !freezeWatchPaused(document.hidden) &&
            sendLooksStalled({ lastSentAt, watchingSince, now })
          ) {
            recover('send_stalled')
          }
        })
        .catch(() => {
          if (!shouldActOnStaleStats(cancelled)) return
          if (
            !video.isMuted &&
            !freezeWatchPaused(document.hidden) &&
            sendLooksStalled({ lastSentAt, watchingSince, now: Date.now() })
          ) {
            recover('send_stalled')
          }
        })
      if (!shouldActOnStaleStats(cancelled)) return
      if (cameraLooksFrozen({ watchingSince, lastFrameAt, now: Date.now() })) {
        recover('frozen')
      }
    }, 500)
    return () => {
      cancelled = true
      clearInterval(tick)
      if (canRafc && rvfc) el.cancelVideoFrameCallback(rvfc)
      video.detach(el)
    }
  }, [enabled, room, recover, state.attempts, publishTick])

  useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current)
    },
    [],
  )

  return { ...state, recover }
}
